/**
 * Dashboard Stats Cards Component
 * Display key statistics for audit log monitoring
 */

import React from "react";
import {
    Grid,
    GridItem,
    Box,
    VStack,
    HStack,
    Text,
    useColorModeValue,
    Skeleton,
    Icon,
} from "@chakra-ui/react";
import { FiActivity, FiCheck, FiX, FiUsers, FiSmartphone } from "react-icons/fi";
import { DashboardStats } from "./types";
import { getTimeRangeDescription } from "./utils";

interface StatsCardsProps {
    stats: DashboardStats | null;
    loading?: boolean;
    timeRange?: string;
}

interface StatCardProps {
    icon: React.ElementType;
    label: string;
    value: string | number;
    subtext?: string;
    colorScheme?: "green" | "blue" | "orange" | "red";
    loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
    icon,
    label,
    value,
    subtext,
    colorScheme = "blue",
    loading = false,
}) => {
    const bg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const labelColor = useColorModeValue("gray.600", "gray.400");
    const valueColor = useColorModeValue("gray.900", "white");

    const colorMap = {
        green: useColorModeValue("green.500", "green.300"),
        blue: useColorModeValue("blue.500", "blue.300"),
        orange: useColorModeValue("orange.500", "orange.300"),
        red: useColorModeValue("red.500", "red.300"),
    };

    return (
        <Box
            bg={bg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
            transition="all 0.2s"
            _hover={{ shadow: "md" }}
        >
            <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                    <Text fontSize="sm" color={labelColor} fontWeight="600">
                        {label}
                    </Text>
                    <Icon as={icon} color={colorMap[colorScheme]} boxSize={5} />
                </HStack>

                <VStack align="flex-start" spacing={1}>
                    {loading ? (
                        <Skeleton height="28px" width="100px" />
                    ) : (
                        <Text fontSize="2xl" fontWeight="bold" color={valueColor}>
                            {value}
                        </Text>
                    )}
                    {subtext && (
                        <Text fontSize="xs" color={labelColor}>
                            {subtext}
                        </Text>
                    )}
                </VStack>
            </VStack>
        </Box>
    );
};

const StatsCards: React.FC<StatsCardsProps> = ({
    stats,
    loading = false,
    timeRange = "last_24_hours",
}) => {
    const textColorMuted = useColorModeValue("gray.600", "gray.400");

    if (loading) {
        return (
            <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }}
                gap={4}
                mb={6}
            >
                {Array.from({ length: 5 }).map((_, i) => (
                    <GridItem key={i}>
                        <StatCard
                            icon={FiActivity}
                            label="Loading"
                            value=""
                            loading={true}
                        />
                    </GridItem>
                ))}
            </Grid>
        );
    }

    if (!stats) {
        return null;
    }

    const successPercentage = stats.success_rate.toFixed(2);
    const failPercentage = (100 - stats.success_rate).toFixed(2);

    return (
        <VStack align="stretch" spacing={6} mb={6}>
            <Box>
                <Text fontSize="sm" color={textColorMuted}>
                    {getTimeRangeDescription(
                        timeRange as any,
                        stats.start_date,
                        stats.end_date
                    )}
                </Text>
            </Box>

            <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }}
                gap={4}
            >
                <GridItem>
                    <StatCard
                        icon={FiActivity}
                        label="Total Login"
                        value={stats.total_logins.toLocaleString("id-ID")}
                        colorScheme="blue"
                    />
                </GridItem>

                <GridItem>
                    <StatCard
                        icon={FiCheck}
                        label="Login Berhasil"
                        value={stats.successful_logins.toLocaleString("id-ID")}
                        subtext={`${successPercentage}%`}
                        colorScheme="green"
                    />
                </GridItem>

                <GridItem>
                    <StatCard
                        icon={FiX}
                        label="Login Gagal"
                        value={stats.failed_logins.toLocaleString("id-ID")}
                        subtext={`${failPercentage}%`}
                        colorScheme="red"
                    />
                </GridItem>

                <GridItem>
                    <StatCard
                        icon={FiUsers}
                        label="Pengguna Unik"
                        value={stats.unique_users.toLocaleString("id-ID")}
                        colorScheme="orange"
                    />
                </GridItem>

                <GridItem>
                    <StatCard
                        icon={FiSmartphone}
                        label="Device Unik"
                        value={stats.unique_devices.toLocaleString("id-ID")}
                        colorScheme="blue"
                    />
                </GridItem>
            </Grid>
        </VStack>
    );
};

export default StatsCards;
